/**
 * 司机登记信息审核页面
 */
import React from 'react';
import { Card, Form, Row, Col, Input, Button, Select, Space } from 'antd';
import { useTable } from '@/utils/useTable';
import CommonTable from '@/components/commonTable';
import styles from '@/styles/pageContent.module.scss';
import { formSearchGutter } from '@/constants/common';
import ExportButton from '@/components/exportButton';
import LinkFaceLibModal from '@/components/linkFaceLibModal';
import ReviewModal from './components/ReviewModal';
import SimilarDriversModal from './components/SimilarDriversModal'; // 导入新弹窗
import { getAllDriverRegistrationInfo } from '@/service/driverArchive';
import { useFleetOptions } from './hooks/useFleetOptions.hook';
import { useReviewStatusOptions } from './hooks/useReviewStatusOptions.hook';
import { useSearchForm } from './hooks/useSearchForm.hook';
import { useModalState } from './hooks/useModalState.hook';
import { useTableOperations } from './hooks/useTableOperations.hook';
import { useTableColumns } from './hooks/useTableColumns.hook';

const { Option } = Select;

/**
 * 司机登记信息审核页面
 */
const DriverRegistrationReviewPage: React.FC = () => {
  const [form] = Form.useForm();

  // 车队选项
  const { fleetOptions, fetchFleetList } = useFleetOptions();

  // 审核状态选项
  const { reviewStatusOptions } = useReviewStatusOptions();

  // 搜索表单
  const { searchParams, onSearch, onReset, getExportParams } = useSearchForm(form);

  // 表格数据hook
  const { page, limit, loading, data, totalCount, onPageChange, getData } = useTable({
    requestFunc: getAllDriverRegistrationInfo,
    searchParams,
    initPageSize: 40,
  });

  // 弹窗状态管理
  const {
    linkFaceLibModalVisible,
    reviewModalVisible,
    reviewModalMode,
    currentRecord,
    similarDriversModalVisible,
    similarDrivers,
    similarDriversCurrentRecord,
    openLinkFaceLibModal,
    closeLinkFaceLibModal,
    openReviewModal,
    openReviewViewModal,
    closeReviewModal,
    openSimilarDriversModal,
    closeSimilarDriversModal,
  } = useModalState();

  // 表格操作逻辑
  const { handleReviewOk, handleLinkFaceLibOk, handleSyncArchive, handleSyncArchiveNew, handleSyncArchiveUpdate } = useTableOperations(
    getData,
    openSimilarDriversModal
  );

  // 表格列配置
  const { columns } = useTableColumns({
    onReview: openReviewModal,
    onViewResult: openReviewViewModal,
    onSyncArchive: handleSyncArchive,
    onLinkFaceLib: openLinkFaceLibModal,
  });

  // 审核弹窗确定
  const onReviewOk = async (values: any) => {
    const success = await handleReviewOk(values);
    if (success) {
      closeReviewModal();
    }
  };

  // 关联人脸库弹窗确定
  const onLinkFaceLibOk = () => {
    handleLinkFaceLibOk();
    closeLinkFaceLibModal();
  };

  // 名称近似司机弹窗确定
  const onSimilarDriversOk = async (selectedDriver: any) => {
    if (selectedDriver === null) {
      // 用户点击"目标司机不在表中，新建数据"
      const success = await handleSyncArchiveNew(similarDriversCurrentRecord);
      if (success) {
        closeSimilarDriversModal();
      }
    } else {
      // 用户选择了一个司机，调用更新逻辑
      const success = await handleSyncArchiveUpdate(similarDriversCurrentRecord, selectedDriver);
      if (success) {
        closeSimilarDriversModal();
      }
    }
  };

  return (
    <Card className={styles.pageContent}>
      <Form form={form} onFinish={onSearch}>
        <Row gutter={formSearchGutter}>
          <Col span={6}>
            <Form.Item name="reviewStatus" label="审核状态">
              <Select placeholder="请选择" allowClear>
                {reviewStatusOptions.map((status:any) => (
                  <Option key={status.value} value={status.value}>
                    {status.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="driverName" label="司机姓名">
              <Input placeholder="请输入司机姓名" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="fleetId" label="所属车队">
              <Select 
                placeholder="请选择" 
                allowClear 
                showSearch
                onSearch={(value) => fetchFleetList(value)}
                filterOption={false}
              >
                {fleetOptions.map((fleet) => (
                  <Option key={fleet.value} value={fleet.value}>
                    {fleet.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={formSearchGutter}>
          <Col span={24}>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  搜索
                </Button>
              
                <Button onClick={onReset}>重置</Button>
                <ExportButton
                  type="default"
                  disabled={!data || data.length === 0}
                  url="/driver/registration/review/export"
                  fileName="司机登记审核信息"
                  getParamsFunc={getExportParams}
                  requestMethod="GET"
                  permissionKey="/driver/registration/review/export"
                >
                  导出
                </ExportButton>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <CommonTable
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey="id"
        scroll={{ x: 3500 }}
        pagination={{
          current: page,
          pageSize: limit,
          total: totalCount,
          onChange: onPageChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
      />
      <LinkFaceLibModal
        visible={linkFaceLibModalVisible}
        onCancel={closeLinkFaceLibModal}
        onOk={onLinkFaceLibOk}
        record={currentRecord}
        fleetOptions={fleetOptions}
        fetchFleetList={fetchFleetList}
      />
      <ReviewModal
        visible={reviewModalVisible}
        mode={reviewModalMode}
        onCancel={closeReviewModal}
        onOk={onReviewOk}
        record={currentRecord}
        fleetOptions={fleetOptions}
        fetchFleetList={fetchFleetList}
      />
      <SimilarDriversModal
        visible={similarDriversModalVisible}
        onCancel={closeSimilarDriversModal}
        onOk={onSimilarDriversOk}
        drivers={similarDrivers}
        currentRecord={similarDriversCurrentRecord}
      />
    </Card>
  );
};

export default DriverRegistrationReviewPage;
